import React from 'react'
import { dashboardGraphFilter } from '../../lib/utils/message'
import { shouldShowWithdrawalProgressCard } from './dashboard-v2.utils'
import { DashboardV2PresenterProps } from './dashboard-v2.types'
import { DashboardHeader } from './components/Header'
import { Stats } from './components/Stats'
import { PerformanceChart } from './components/PerformanceChart'
import { InvestmentsTabs } from './components/InvestmentsTabs'
import { DistributionChart } from './components/DistributionChart'
import { HoldingsGrid } from './components/HoldingsGrid'
import { TransactionsTable } from './components/TransactionsTable'
import { ProfileCard } from './components/ProfileCard'
import { OngoingInvestmentsSection } from './components/OngoingInvestmentsSection'
import { WithdrawalsSection } from './components/WithdrawalsSection'
import { PublicPortfolioSection } from './components/PublicPortfolioSection'
import { isFeatureEnabled } from '../../lib/utils/feature-flags'

const DashboardV2Presenter = ({
  investorDisplayName,
  investorInitial,
  kycStatus,
  clientId,
  dashboardSummary,
  isStatementsMenuOpen,
  portfolioStatements,
  portfolioStatementsLoading,
  downloadingStatementId,
  investmentView,
  holdingsData = [],
  totalInvestment,
  investmentLimit,
  usedPct,
  remaining,
  activeHoldings,
  perfSeriesDollars,
  currentValueText,
  lastUpdatedText,
  distributionItems,
  statusBreakdownData,
  holdingsSnapshotData,
  selectedRange,
  onRangeChange,
  transactionsData = [],
  onTransactionClick,
  ongoingInvestmentsData = [],
  onOngoingInvestmentContinue,
  onOngoingInvestmentViewDetails,
  onOngoingInvestmentCancel,
  withdrawalsData = [],
  onWithdrawalViewDetails,
  onWithdrawalCancel,
  onToggleStatements,
  onDownloadStatement,
  onNewInvestment,
  showSecondaryMarketLink,
  onSecondaryMarket,
  onInvestmentViewChange,
  onHoldingClick,
  onHoldingMenuAction,
  onRequestLimitExtension,
  hasPendingLimitRequest,
  onWallet,
}: DashboardV2PresenterProps) => {
  const hasOngoingInvestments = ongoingInvestmentsData.length > 0
  const hasWithdrawalsToShow = withdrawalsData.some((w) =>
    shouldShowWithdrawalProgressCard(w.status),
  )

  return (
    <div className='dashboard-v2'>
      <DashboardHeader
        isStatementsMenuOpen={isStatementsMenuOpen}
        statements={portfolioStatements}
        statementsLoading={portfolioStatementsLoading}
        downloadingStatementId={downloadingStatementId}
        onToggleStatements={onToggleStatements}
        onDownloadStatement={onDownloadStatement}
        onNewInvestment={onNewInvestment}
        showSecondaryMarketLink={showSecondaryMarketLink}
        onSecondaryMarket={onSecondaryMarket}
      />

      <section className='dashboard-v2__top-grid'>
        <ProfileCard
          investorInitial={investorInitial}
          investorDisplayName={investorDisplayName}
          kycStatus={kycStatus}
          clientId={clientId}
          totalInvestment={totalInvestment}
          investmentLimit={investmentLimit}
          usedPct={usedPct}
          remaining={remaining}
          activeHoldings={activeHoldings}
          onRequestLimitExtension={onRequestLimitExtension}
          hasPendingLimitRequest={hasPendingLimitRequest}
          onWallet={onWallet}
        />

        <Stats summary={dashboardSummary} />
      </section>

      <PerformanceChart
        title='Portfolio Value Performance'
        filters={dashboardGraphFilter}
        selected={selectedRange}
        onChange={onRangeChange}
        series={perfSeriesDollars}
        currentValueText={currentValueText}
        lastUpdatedText={lastUpdatedText}
        transactions={transactionsData}
      />

      <section className='dashboard-v2__dual-grid'>
        <InvestmentsTabs
          view={investmentView}
          onChange={onInvestmentViewChange}
          statusData={statusBreakdownData}
          snapshotData={holdingsSnapshotData}
        />
        <DistributionChart title='Investment Distribution' items={distributionItems} />
      </section>

      {hasOngoingInvestments ? (
        <OngoingInvestmentsSection
          investments={ongoingInvestmentsData}
          onInvestmentContinue={onOngoingInvestmentContinue}
          onInvestmentViewDetails={onOngoingInvestmentViewDetails}
          onInvestmentCancel={onOngoingInvestmentCancel}
        />
      ) : null}

      <HoldingsGrid
        holdings={holdingsData}
        onHoldingClick={onHoldingClick}
        onHoldingMenuAction={onHoldingMenuAction}
      />

      {isFeatureEnabled('trading') || isFeatureEnabled('publicWallet') ? (
        <PublicPortfolioSection />
      ) : null}

      {hasWithdrawalsToShow ? (
        <WithdrawalsSection
          withdrawals={withdrawalsData}
          onWithdrawalViewDetails={onWithdrawalViewDetails}
          onWithdrawalCancel={onWithdrawalCancel}
        />
      ) : null}

      <TransactionsTable transactions={transactionsData} onTransactionClick={onTransactionClick} />
    </div>
  )
}

export { DashboardV2Presenter }
